import {
  GreyGrey60,
  GreyGrey80,
  IconCircleX,
  IconExclamationCircle,
  IndigoIndigo60,
  RedRed60,
  Spinner,
  Tooltip,
  Box
} from '@sequencehq/core-components'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { MAX_FILE_SIZE } from 'Assets/constants'
import { bytesToMegabyteString } from 'Assets/utils'
import { FC, useEffect, useState } from 'react'
import { match } from 'ts-pattern'
import { Asset } from '../attachmentReducer'
import { AttachmentUI } from './AttachmentUI'

const uploadFile = async (file: File) => {
  const formData = new FormData()
  formData.append('file', file)

  return await dashboard20240730Client.postAssets({
    formData
  })
}

type UploadStatus = 'UPLOADING' | 'API_ERROR' | 'FILE_SIZE_TOO_LARGE'

export const NewAttachment: FC<{
  file: File
  clientId: string
  onUploadSuccess: (asset: Asset, clientId: string) => void
  onClickDelete: () => void
  maxFileSize?: number
}> = ({
  file,
  onUploadSuccess,
  onClickDelete,
  clientId,
  maxFileSize = MAX_FILE_SIZE
}) => {
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>(
    file.size >= maxFileSize ? 'FILE_SIZE_TOO_LARGE' : 'UPLOADING'
  )

  useEffect(() => {
    if (uploadStatus !== 'UPLOADING') {
      return
    }

    uploadFile(file)
      .then(res => {
        if (res.error) {
          setUploadStatus('API_ERROR')
          return
        }

        res.data && onUploadSuccess(res.data, clientId)
      })
      .catch(() => setUploadStatus('API_ERROR'))
  }, [file, onUploadSuccess, clientId, uploadStatus])

  return (
    <AttachmentUI
      id={clientId}
      name={file.name}
      fileSizeBytes={file.size}
      icon={match(uploadStatus)
        .with('UPLOADING', () => (
          <Spinner width="16px" height="16px" color={IndigoIndigo60} />
        ))
        .with('API_ERROR', () => (
          <Tooltip label={'Upload failed'}>
            <IconExclamationCircle color={RedRed60} />
          </Tooltip>
        ))
        .with('FILE_SIZE_TOO_LARGE', () => (
          <Tooltip
            label={`File size too large. Maximum ${bytesToMegabyteString(maxFileSize)}`}
          >
            <IconExclamationCircle color={RedRed60} />
          </Tooltip>
        ))
        .exhaustive()}
      controls={
        <Box
          color={GreyGrey60}
          _hover={{ color: GreyGrey80 }}
          paddingX={1}
          onClick={e => {
            e.stopPropagation()
            onClickDelete()
          }}
          cursor="pointer"
        >
          <IconCircleX />
        </Box>
      }
    />
  )
}
